import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button, Col, Row, Table, Tooltip } from 'antd';
import { ClockCircleOutlined, DownOutlined, UpOutlined, UploadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Box } from 'atoms';
import { AntdTable } from 'components/custom-table/Style';
import StatusChip from 'componentsv2/common/StatusChip';
import { useWarehousePermissions } from 'hooks';
import { numberWithCommas } from 'utils/NumberCommaSeprator';
// Leaf constants module shared with the project setup form — no cycle risk
import { CHARGE_BASIS_PER_BAG } from 'componentsv2/forms/project/warehouseRevenue';
import {
	createWarehouseDailyBagV2,
	createWarehouseDailyManpowerV2,
	createWarehouseDailyShipmentV2,
	deleteWarehouseDailyEntryV2,
	updateWarehouseDailyCountV2,
	searchWarehouseDailyEntriesV2,
	searchWarehouseManpowerTypesV2,
} from 'store/warehousingSliceV2';
import {
	AGING_ON_TIME,
	LINE_ITEM_BAG,
	LINE_ITEM_MANPOWER,
	LINE_ITEM_SHIPMENT,
	MODEL_COST_PLUS,
	mergeSlabBreakdowns,
	uploadPanelColSpan,
} from '../warehousing.constants';
import DailyQuickAdd from './DailyQuickAdd';
import DailyDatePanel from './DailyDatePanel';
import WarehouseCsvUploadModal from './WarehouseCsvUploadModal';
import {
	EmptyState,
	ErrorState,
	ExpandToggle,
	NumCell,
	Panel,
	PanelHeader,
	PanelSubtitle,
	PanelTitle,
} from './warehousingStyles';

const money = (value) =>
	value === null || value === undefined ? '—' : `₹ ${numberWithCommas(Math.round(value))}`;

const num = (value) => numberWithCommas(Number(value) || 0);

const ACTION_BY_MODE = {
	[LINE_ITEM_MANPOWER]: createWarehouseDailyManpowerV2,
	[LINE_ITEM_SHIPMENT]: createWarehouseDailyShipmentV2,
	[LINE_ITEM_BAG]: createWarehouseDailyBagV2,
};

/**
 * A date's manpower cost to the customer (Cost Plus). A line with no Cost to Customer set
 * reads as equal to its Cost to Company, so it falls back to `cost` — the same basis as the
 * date's `manpower_cost` column beside it. The API's own `manpower_cost_to_customer` skips
 * unset lines, which would understate this column.
 */
const dateCostToCustomer = (date) =>
	(date?.line_items || [])
		.filter((item) => item.type === LINE_ITEM_MANPOWER)
		.reduce((sum, item) => sum + (Number(item.cost_to_customer ?? item.cost) || 0), 0);

export default function DailyTab({
	projectId,
	projectName,
	periodMonth,
	dashboard,
	onDataChanged,
}) {
	const dispatch = useDispatch();
	const { canAddDailyEntry, canUploadDailyCsv, canEditDailyCount, canDeleteDailyEntry } =
		useWarehousePermissions();

	const costFrozen = Boolean(dashboard?.cost_frozen);
	const revenueFrozen = Boolean(dashboard?.revenue_frozen);
	const modelType = dashboard?.revenue_model?.model_type;
	const isCostPlus = modelType === MODEL_COST_PLUS;
	// Freeze is scoped to the ONE month selected above — name it explicitly so nobody
	// reads a frozen badge as "the whole project is locked".
	const monthLabel = periodMonth.format('MMMM YYYY');
	const costFrozenReason = `Cost is frozen for ${monthLabel} — no further entries`;
	const revenueFrozenReason = `Revenue is frozen for ${monthLabel} — no further entries`;

	// Bag counts exist only to be billed as Cross Dock (Bag Count × rate). A project
	// without that rate configured collects no bag counts at all — entering them would
	// bank numbers that earn nothing, and would start billing retroactively if a rate
	// were added later. Matched on basis, never on the head's name.
	const perBagCharge = (dashboard?.additional_charges || []).find(
		(charge) => charge.basis === CHARGE_BASIS_PER_BAG
	);
	const bagRate = perBagCharge ? Number(perBagCharge.rate) : undefined;
	const bagsEnabled = bagRate !== undefined && bagRate > 0;
	// Cost Plus bills on cost, not on shipments, so it has no Shipments panel, column or total.
	const showShipments = !isCostPlus;
	const uploadPanelSpan = uploadPanelColSpan(1 + (showShipments ? 1 : 0) + (bagsEnabled ? 1 : 0));

	const [dates, setDates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [saving, setSaving] = useState(false);
	const [uploadMode, setUploadMode] = useState(null);
	// Controlled, not defaultExpandedRowKeys: dates arrive after mount, so a default
	// would be computed from an empty list and never re-applied.
	const [expandedKeys, setExpandedKeys] = useState([]);

	const fromDate = useMemo(
		() => periodMonth.clone().startOf('month').format('YYYY-MM-DD'),
		[periodMonth]
	);
	const toDate = useMemo(
		() => periodMonth.clone().endOf('month').format('YYYY-MM-DD'),
		[periodMonth]
	);

	const fetchEntries = useCallback(async () => {
		setLoading(true);
		setLoadError(false);
		try {
			// One call returns both manpower and shipment line items, each tagged with a
			// `type` — there is no server-side entry_type filter.
			const result = await dispatch(
				searchWarehouseDailyEntriesV2({
					filter_by: { project_id: projectId, from_date: fromDate, to_date: toDate },
				})
			);
			const rows = [...(result?.dates || [])].sort((a, b) =>
				String(b.entry_date).localeCompare(String(a.entry_date))
			);
			setDates(rows);
		} catch (error) {
			setLoadError(true);
		} finally {
			setLoading(false);
		}
	}, [dispatch, projectId, fromDate, toDate]);

	useEffect(() => {
		fetchEntries();
	}, [fetchEntries]);

	// Open the most recent date once entries land
	useEffect(() => {
		setExpandedKeys(dates.length ? [dates[0].entry_date] : []);
	}, [dates]);

	// The project's seeded manpower type catalog, including which types accept hours
	const [manpowerTypes, setManpowerTypes] = useState([]);

	useEffect(() => {
		dispatch(searchWarehouseManpowerTypesV2({ project_id: projectId }))
			.then((types) => setManpowerTypes(types || []))
			.catch(() => setManpowerTypes([]));
	}, [dispatch, projectId]);

	const handleAdd = async (mode, payload) => {
		setSaving(true);
		try {
			const action = ACTION_BY_MODE[mode];
			await dispatch(action({ req_data: { project_id: projectId, ...payload } }));
			await fetchEntries();
			onDataChanged();
			return true;
		} catch (error) {
			return false;
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (entryType, id) => {
		try {
			await dispatch(
				deleteWarehouseDailyEntryV2({ project_id: projectId, entry_type: entryType, id })
			);
			await fetchEntries();
			onDataChanged();
		} catch (error) {
			// Errors surface through the API interceptor
		}
	};

	const handleUpdateCount = async ({ entryType, entryId, count }) => {
		try {
			await dispatch(
				updateWarehouseDailyCountV2({
					project_id: projectId,
					entry_type: entryType,
					id: entryId,
					count,
				})
			);
			// Refetch rather than patch locally: the edited figure feeds MTD revenue, so the
			// header stats have to be recomputed by the server too.
			await fetchEntries();
			onDataChanged();
		} catch (error) {
			// Errors surface through the API interceptor
		}
	};

	const handleUploadDone = async () => {
		await fetchEntries();
		onDataChanged();
	};

	const totals = useMemo(
		() =>
			dates.reduce(
				(acc, d) => ({
					manpowerCost: acc.manpowerCost + (Number(d.manpower_cost) || 0),
					manpowerCostToCustomer: acc.manpowerCostToCustomer + dateCostToCustomer(d),
					headcount: acc.headcount + (Number(d.headcount) || 0),
					otHours: acc.otHours + (Number(d.ot_hours) || 0),
					shipments: acc.shipments + (Number(d.shipments) || 0),
					// `bags` is not yet returned per-date by the search endpoint (see the
					// BAGS column comment below) — this sums whatever is present today,
					// which is nothing, until the backend response is extended.
					bags: acc.bags + (Number(d.bags) || 0),
				}),
				{
					manpowerCost: 0,
					manpowerCostToCustomer: 0,
					headcount: 0,
					otHours: 0,
					shipments: 0,
					bags: 0,
				}
			),
		[dates]
	);

	const columns = [
		{ title: 'Date', dataIndex: 'entry_date', key: 'entry_date' },
		{
			title: isCostPlus ? 'Manpower Cost to Company' : 'Manpower Cost',
			dataIndex: 'manpower_cost',
			key: 'manpower_cost',
			align: 'right',
			render: (v) => <NumCell>{money(v)}</NumCell>,
		},
		...(isCostPlus
			? [
					{
						title: 'Manpower Cost to Customer',
						key: 'manpower_cost_to_customer',
						align: 'right',
						render: (_, record) => (
							<NumCell>{money(dateCostToCustomer(record))}</NumCell>
						),
					},
			  ]
			: []),
		{
			title: 'Headcount',
			dataIndex: 'headcount',
			key: 'headcount',
			align: 'right',
			render: (v) => <NumCell>{num(v)}</NumCell>,
		},
		{
			title: 'OT Hours',
			dataIndex: 'ot_hours',
			key: 'ot_hours',
			align: 'right',
			render: (v) => <NumCell>{Number(v) > 0 ? num(v) : '—'}</NumCell>,
		},
		...(showShipments
			? [
					{
						title: 'Shipments',
						dataIndex: 'shipments',
						key: 'shipments',
						align: 'right',
						render: (v) => <NumCell>{num(v)}</NumCell>,
					},
			  ]
			: []),
		...(bagsEnabled
			? [
					{
						title: 'Bags',
						dataIndex: 'bags',
						key: 'bags',
						align: 'right',
						render: (v) => (
							<NumCell>{v === undefined || v === null ? '—' : num(v)}</NumCell>
						),
					},
			  ]
			: []),
		{
			title: 'Aging',
			dataIndex: 'aging',
			key: 'aging',
			// The chip is inline-flex with a gap, so the clock sits inside the pill
			// alongside the text rather than needing a wrapper of its own.
			render: (v, record) => {
				if (v === AGING_ON_TIME) {
					return (
						<StatusChip
							tone="success"
							label={
								<>
									<ClockCircleOutlined />
									On time
								</>
							}
						/>
					);
				}
				const late = Number(record.days_late) || 0;
				return (
					<StatusChip
						tone="danger"
						label={
							<>
								<ClockCircleOutlined />
								{late > 0 ? `+${late} ${late === 1 ? 'day' : 'days'} late` : 'Late'}
							</>
						}
					/>
				);
			},
		},
	];

	// One summary cell per column, so indices shift when the Cost Plus customer column is
	// present — `next` hands them out in column order.
	const renderSummary = () => {
		let col = 0;
		const next = () => {
			col += 1;
			return col;
		};
		const cell = (content) => (
			<Table.Summary.Cell index={next()} align="right">
				<NumCell>
					<strong>{content}</strong>
				</NumCell>
			</Table.Summary.Cell>
		);
		return (
			<Table.Summary fixed>
				<Table.Summary.Row>
					<Table.Summary.Cell index={0} />
					<Table.Summary.Cell index={next()}>
						<strong>Grand Total</strong>
					</Table.Summary.Cell>
					{cell(money(totals.manpowerCost))}
					{isCostPlus && cell(money(totals.manpowerCostToCustomer))}
					{cell(num(totals.headcount))}
					{cell(num(totals.otHours))}
					{showShipments && cell(num(totals.shipments))}
					{bagsEnabled && cell(totals.bags > 0 ? num(totals.bags) : '—')}
					<Table.Summary.Cell index={next()} />
				</Table.Summary.Row>
			</Table.Summary>
		);
	};

	const renderEntries = () => {
		if (loadError) {
			return (
				<ErrorState>
					<Box mb="12px">Could not load daily entries.</Box>
					<Button onClick={fetchEntries}>Retry</Button>
				</ErrorState>
			);
		}

		if (!loading && dates.length === 0) {
			return (
				<EmptyState>
					<div className="empty-title">
						No entries for {periodMonth.format('MMMM YYYY')}
					</div>
					<div className="empty-body">
						Add a row above or upload a sheet to start tracking this month.
					</div>
				</EmptyState>
			);
		}

		return (
			<AntdTable
				rowKey="entry_date"
				columns={columns}
				dataSource={dates}
				loading={loading}
				pagination={false}
				scroll={{ x: 900 }}
				expandable={{
					expandedRowKeys: expandedKeys,
					onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
					// Chevron rather than antd's default +/- square
					// eslint-disable-next-line react/no-unstable-nested-components
					expandIcon: ({ expanded, onExpand, record }) => (
						<ExpandToggle
							type="button"
							aria-label={expanded ? 'Collapse date' : 'Expand date'}
							onClick={(e) => onExpand(record, e)}
						>
							{expanded ? <UpOutlined /> : <DownOutlined />}
						</ExpandToggle>
					),
					// antd's expandable API takes a render function by design
					// eslint-disable-next-line react/no-unstable-nested-components
					expandedRowRender: (record) => {
						// `dates` is sorted newest-first (see fetchEntries), so "month-to-date
						// through this row" is this row plus everything AFTER it in the array —
						// all older dates, still within the same periodMonth this tab already
						// scopes `dates` to, which is what a slab month-to-date figure requires.
						const idx = dates.findIndex((d) => d.entry_date === record.entry_date);
						const upToThisDate = idx >= 0 ? dates.slice(idx) : [record];
						const cumulativeBreakdown = mergeSlabBreakdowns(
							upToThisDate.map((d) => d.slab_breakdown)
						);
						const cumulativeShipmentCount = upToThisDate.reduce(
							(sum, d) => sum + (Number(d.shipments) || 0),
							0
						);
						return (
							<DailyDatePanel
								date={record}
								cumulativeBreakdown={cumulativeBreakdown}
								cumulativeShipmentCount={cumulativeShipmentCount}
								canDelete={canDeleteDailyEntry}
								canEditCounts={canEditDailyCount}
								costFrozen={costFrozen}
								revenueFrozen={revenueFrozen}
								revenueFrozenReason={revenueFrozenReason}
								bagRate={bagRate}
								bagRateLabel={perBagCharge?.charge_head}
								isCostPlus={isCostPlus}
								onDelete={handleDelete}
								onUpdateCount={handleUpdateCount}
							/>
						);
					},
				}}
				// antd's summary API takes a render function by design
				// eslint-disable-next-line react/no-unstable-nested-components
				summary={renderSummary}
			/>
		);
	};

	const uploadPanel = (mode, title, frozen, frozenMessage, frozenTooltip) => (
		<Panel>
			<PanelHeader>
				<Box style={{ flex: '1 1 240px' }}>
					<PanelTitle>{title}</PanelTitle>
				</Box>
				{canUploadDailyCsv(mode) && (
					<Tooltip title={frozen ? frozenTooltip : undefined}>
						<Button
							icon={<UploadOutlined />}
							disabled={frozen}
							onClick={() => setUploadMode(mode)}
						>
							Upload Sheet
						</Button>
					</Tooltip>
				)}
			</PanelHeader>

			{canAddDailyEntry(mode) && (
				<>
					<DailyQuickAdd
						mode={mode}
						projectName={projectName}
						manpowerTypes={manpowerTypes}
						periodMonth={periodMonth}
						saving={saving}
						disabled={frozen}
						disabledReason={frozenTooltip}
						isCostPlus={isCostPlus}
						onSubmit={(payload) => handleAdd(mode, payload)}
					/>
					{frozen && (
						<PanelSubtitle style={{ marginTop: 8 }}>{frozenMessage}</PanelSubtitle>
					)}
				</>
			)}
		</Panel>
	);

	return (
		<>
			<Row gutter={[24, 24]}>
				<Col {...uploadPanelSpan}>
					{uploadPanel(
						LINE_ITEM_MANPOWER,
						'Manpower Upload',
						costFrozen,
						`Cost is frozen for ${monthLabel} — manpower entries are closed for that month.`,
						costFrozenReason
					)}
				</Col>
				{showShipments && (
					<Col {...uploadPanelSpan}>
						{uploadPanel(
							LINE_ITEM_SHIPMENT,
							'Shipments Upload',
							revenueFrozen,
							`Revenue is frozen for ${monthLabel} — shipment entries are closed for that month.`,
							revenueFrozenReason
						)}
					</Col>
				)}
				{bagsEnabled && (
					<Col {...uploadPanelSpan}>
						{uploadPanel(
							LINE_ITEM_BAG,
							'Bag Count Upload',
							revenueFrozen,
							`Revenue is frozen for ${monthLabel} — bag entries are closed for that month.`,
							revenueFrozenReason
						)}
					</Col>
				)}
			</Row>

			<Box mt="24px">
				<Panel>
					<PanelHeader>
						<Box>
							<PanelTitle>Daily Entries</PanelTitle>
							<PanelSubtitle>
								{`Expand a date to see its manpower${
									showShipments ? ', shipment' : ''
								} and bag line items.`}
							</PanelSubtitle>
						</Box>
					</PanelHeader>

					{renderEntries()}
				</Panel>
			</Box>

			{uploadMode && (
				<WarehouseCsvUploadModal
					open
					entryType={uploadMode}
					projectId={projectId}
					modelType={modelType}
					onCancel={() => setUploadMode(null)}
					onDone={handleUploadDone}
				/>
			)}
		</>
	);
}

DailyTab.propTypes = {
	projectId: PropTypes.number.isRequired,
	projectName: PropTypes.string,
	periodMonth: PropTypes.instanceOf(moment).isRequired,
	dashboard: PropTypes.object,
	onDataChanged: PropTypes.func.isRequired,
};

DailyTab.defaultProps = {
	projectName: '',
	dashboard: null,
};
